import Auth from '../Auth';
import SigV4Client from '../Util/SigV4Client';
import { logger } from '../Util/Logger';
import { getEnvConfigByKey } from './EnvConfig';
import { DashboardBanner, NotificationConfig } from '../Types/DashboardBanner';

const storage = window.localStorage;
export const storageNamespace = 'PerformancePortalFeatureConfig';
const featureConfigUrl =
  getEnvConfigByKey('featureConfigUrl') ||
  process.env.REACT_APP_APP_CONFIG_URL ||
  '';
const configTTL = 1800;
const USER_FEATURES = 'features_USER';

const FEATURES = 'features';
const EXPIRATION = 'expiration';
const ENABLED = 'enabled';

export enum Feature {
  NEW_COACHING = 'newCoaching',
  CONTACT_RECOMMENDER = 'contactRecommender',
  LIGHTHOUSE = 'lighthouse', // True everywhere
  EMAIL_COACHING_NOTIFICATION = 'emailCoachingNotification',
  DASHBOARD_NOTIFICATIONS = 'dashboardNotifications',
  DASHBOARD_ALERT = 'dashboardAlert',
  TEAM_NOTIFICATION = 'teamNotification',
  TEAM_ALERT = 'teamAlert',
  CONTACT_LINKS = 'contactLinks',
  ACHT_FLAG = 'achtFlag',
  INSPIRE_ACCESS_BLOCKED = 'inspireAccessBlocked',
  TEAM_PROXY = 'teamProxy',
  FEEDS_DELAY = 'feedsDelay',
  NOTIFICATION_CENTER = 'notificationCenter',
  CONCESSION_RATE_METRIC = 'concessionRateMetric',
  CUSTOM_DATE_RANGE = 'customDateRange',
  EMAIL_CONTACT_REVIEW_NOTIFICATION = 'emailContactReviewNotification',
  GROW_MODEL = 'growModel',
  CONTACT_LISTENING = 'contactListening',
  CONTACT_LISTENING_WITH_CHAT = 'contactListeningWithChat',
  CONTACT_LISTENING_MULTITASK = 'contactListeningMultitask',
  CONTACT_TIMELINE = 'enableContactTimeline',
  PROFANITY_FILTER = 'enableProfanityFilter',
  HIDE_CUSTOMER_COMMENT = 'enableHideCustomerComment',
  CONTACT_LISTENING_WITH_NEW_AUTH_FLOW = 'contactListeningWithNewAuthFlow',
  SA_COACHING = 'SACoaching',
  CONTACT_LISTENING_WITH_EMAIL = 'contactListeningWithEmail',
  ENABLE_RAP_REMOVAL_AND_CCX_UPDATE = 'enableRAPRemovalAndCCXUpdate',
  CASE_MANAGEMENT = 'caseManagement',
  ENABLE_CONCESSION_COMPLIANCE = 'enableConcessionCompliance',
  ENABLE_HISTORICAL_CCX_DETAIL = 'enableHistoricalCCXDetails',
  ENABLE_SDS_FORM = 'enableSDSForm',
  ENABLE_SDS_RECIPIENT_FORM_UPDATES = 'enableSDSRecipientFormUpdates',
  ENABLE_SDS_OFFROAD_DRIVER_FORM_UPDATES = 'enableSDSOffRoadDriverFormUpdates',
  ENABLE_SDS_DSL_FORM = 'enableSDSDSLForm',
  OUTLIER_MANAGEMENT = 'outlierManagement',
  TRAINING = 'enableTrainingExperience',
  TREND_METRICS = 'enableTrendMetrics',
  GOAL_SETTING = 'enableGoalSetting',
  GOAL_SETTING_TEAM = 'enableGoalSettingTeam',
  ASSOCIATE_RICH_CARDS = 'enableAssociateRichCards',
  REVIEW_REAL_TIME_HMD = 'enableRealtimeHMDReview',
  RESOURCE_HUB = 'enableResourceHub',
  CAP_REVIEW_FORM = 'enableCapReviewForm',
  CCX_BOTTOM_TWO_BOX = 'enableBottomTwoBox',
  COACHING_STATS_V2 = 'enableCoachingStatsV2',
  OM_COACHING_PAGE = 'omCoachingPage',
  OM_METRICS_PAGE = 'omMetricsPage',
  OM_DASHBOARD_PAGE = 'omDashboardPage',
  OM_COACHING_COMMENTS = 'omCoachingComments',
  CCP_LINK = 'enableCCPLink', // Initial GxTmGroup (India)
  CCP_LINK_LATAM = 'enableCCPLinkLatam', // gx-pact-tm (LATAM)
  CSA_SUMMARIES_PILOT = 'enableCSASummaries',
  CSA_SUMMARIES_ROLLOUT = 'enableCSASummariesRollout',
  CSA_SUMMARIES_WC_APPROVED = 'CSASummariesWCApproved',
  CSA_SUMMARIES_TRANSLATIONS = 'CSASummariesTranslations',
  HISTORICAL_METRICS = 'historicalMetricsEnabled',
  DONT_SHOW_HISTORICAL_METRICS_MODAL = 'dontShowHistoricalMetricsModal',
  CSA_ACHT = 'csaACHTEnabled',
  HIGH_SILENCE_RATE = 'enableHighSilence',
  CSA_CONTACT_LISTENING = 'enableCsaContactListening',
  ENABLE_GOAL_SETTING_ACHT_HOLD_TIME = 'enableGoalSettingACHTHoldTime',
  HIDE_HOLD_RECOMMENDATIONS = 'enableHideHoldRecommendations',
  ENABLE_D2_FORM = 'enableD2Form',
  COACH_AI = 'coachAi',
  ENABLE_DART_FORM = 'enableDartForm',
  ENABLE_CCX_REMOVAL = 'enableCCXRemoval',
}

const FeatureConfig = {
  updateUserItem: (
    userId: string,
    expiration: string,
    response?: any
  ): void => {
    let currentValue = getJson(FeatureConfig.getItem(USER_FEATURES));
    if (!(userId in currentValue)) {
      currentValue[userId] = {};
    }
    if (response) {
      currentValue[userId][FEATURES] = response.features;
    }
    currentValue[userId][EXPIRATION] = expiration;
    FeatureConfig.setItem(USER_FEATURES, JSON.stringify(currentValue));
  },
  getItem: (item: string): string | null => {
    return storage.getItem(`${storageNamespace}_${item}`);
  },
  setItem: (item: string, value: string): void => {
    storage.setItem(`${storageNamespace}_${item}`, value);
  },
  removeItem: (item: string): void => {
    storage.removeItem(`${storageNamespace}_${item}`);
  },
  getConfig: async (userId: string): Promise<void> => {
    // Do Nothing
    if (featureConfigUrl !== '') {
      const response = await fetchConfig(userId);
      if (response?.status === 200) {
        const data = await response.json();
        FeatureConfig.setItem(FEATURES, JSON.stringify(data.features));
        FeatureConfig.setItem(EXPIRATION, getExpirationString());
      }
    }
  },
  hasValidConfig: (userId?: string): boolean => {
    let expiration;
    if (userId) {
      const userConfigById: any = getConfigForUser(userId);
      if (userConfigById && EXPIRATION in userConfigById) {
        expiration = userConfigById[EXPIRATION];
      }
    } else {
      expiration = FeatureConfig.getItem(EXPIRATION);
    }
    return (
      expiration !== null &&
      parseInt(expiration, 10) > Math.floor(Date.now() / 1000)
    );
  },
  refreshConfig: async (userId: string): Promise<boolean> => {
    if (!FeatureConfig.hasValidConfig()) {
      await FeatureConfig.getConfig(userId);
    }
    return true;
  },
  refreshUserConfig: async (
    userId: string,
    specificFeatures?: Feature[]
  ): Promise<boolean> => {
    if (!FeatureConfig.hasValidConfig(userId)) {
      await setUserConfig(userId, specificFeatures);
      const userConfig: any = getJson(FeatureConfig.getItem(USER_FEATURES));
      const updatedUserConfig = removeUserConfigByExpiration(userConfig);
      FeatureConfig.setItem(USER_FEATURES, JSON.stringify(updatedUserConfig));
    }
    return true;
  },
  resetConfig: () => {
    // This will ensure fresh feature configs
    FeatureConfig.removeItem(EXPIRATION);
    FeatureConfig.removeItem(FEATURES);
    FeatureConfig.removeItem(USER_FEATURES);
    window.location.reload();
  },
};

export default FeatureConfig;

export const getNotificationContentFromConfig = (
  feature: Feature | string
): NotificationConfig | null => {
  const featuresJson = FeatureConfig.getItem(FEATURES) || '';
  try {
    const features = featuresJson ? JSON.parse(featuresJson) : {};
    if (feature in features && ENABLED in features[feature]) {
      if (features[feature].enabled) {
        const translateInfo = {
          defaultContent: features[feature].defaultContent,
          linkHref: features[feature].linkHref,
          linkText: features[feature].linkText,
        };
        return translateInfo;
      }
    }
  } catch (e) {
    logger.error('invalid appConfig output');
  }

  return null;
};

export const getAlertContentFromConfig = (feature: Feature): string | null => {
  const featuresJson = FeatureConfig.getItem(FEATURES) || '';
  try {
    const features = featuresJson ? JSON.parse(featuresJson) : {};
    if (feature in features && ENABLED in features[feature]) {
      if (features[feature].enabled) {
        return features[feature].defaultContent;
      }
    }
  } catch (e) {
    logger.error('invalid appConfig output');
  }

  return null;
};

export const isFeatureEnabled = (feature: Feature | string): boolean => {
  const featuresJson = FeatureConfig.getItem(FEATURES) || '';
  try {
    const features = featuresJson ? JSON.parse(featuresJson) : {};
    const appConfigValue = getCurrentConfig(features, feature);

    if (appConfigValue !== null) {
      return appConfigValue;
    }
  } catch (e) {
    logger.error('invalid appConfig output');
  }
  return getDefaultConfig(feature);
};

const FeaturesForCSAByManager = {
  [Feature.CSA_SUMMARIES_ROLLOUT]: Feature.CSA_SUMMARIES_ROLLOUT,
} as const;

type FeatureForCSAByManagerType =
  (typeof FeaturesForCSAByManager)[keyof typeof FeaturesForCSAByManager];

export const isFeatureEnabledForCSAByManager = async (
  feature: FeatureForCSAByManagerType,
  userId: string
) => {
  return await isFeatureEnabledForUser(
    feature,
    userId,
    Object.values(FeaturesForCSAByManager)
  );
};

// this function SHOULD be called with specificFlags, as otherwise we may hit Brass TPS limits
const isFeatureEnabledForUser = async (
  feature: Feature | string,
  userId: string,
  specificFlags?: Feature[]
): Promise<boolean> => {
  // calling refresh here to lazy load the config as not many features use this
  await FeatureConfig.refreshUserConfig(userId, specificFlags);

  const userConfig: any = getConfigForUser(userId);
  if (userConfig) {
    const appConfigValue = getCurrentConfig(userConfig[FEATURES], feature);

    if (appConfigValue !== null) {
      return appConfigValue;
    }
  }
  return getDefaultConfig(feature);
};

const getCurrentConfig = (features: any, option: string): boolean | null => {
  if (option in features && ENABLED in features[option]) {
    return features[option].enabled;
  }

  return null;
};

const getConfigForUser = (userId: string): string | null => {
  const featuresByUser = getJson(FeatureConfig.getItem(USER_FEATURES));
  if (userId in featuresByUser) {
    return featuresByUser[userId];
  }
  return null;
};

const setUserConfig = async (
  userId: string,
  specificFlags?: Feature[]
): Promise<boolean | null> => {
  try {
    const response = await fetchConfig(userId, specificFlags);
    if (response?.status === 200) {
      const data = await response.json();
      FeatureConfig.updateUserItem(userId, getExpirationString(), data);
    } else if (response?.status === 204) {
      FeatureConfig.updateUserItem(userId, getExpirationString());
    }
  } catch (e) {
    logger.error('invalid appConfig output');
  }

  return null;
};

/*
 * In the case that AppConfig is down features will default to false,
 * only define a feature default if it should be true or dependent on the region
 */
const getDefaultConfig = (feature: Feature | string) => {
  const region = getEnvConfigByKey('region');
  if (
    feature === Feature.NEW_COACHING ||
    feature === Feature.CONTACT_RECOMMENDER ||
    feature === Feature.LIGHTHOUSE ||
    feature === Feature.ACHT_FLAG
  ) {
    return region !== 'eu-west-1';
  }
  if (feature === Feature.EMAIL_COACHING_NOTIFICATION) {
    return region !== 'cn-north-1';
  }
  if (feature === Feature.CONTACT_LINKS) {
    return true;
  }
  if (feature === Feature.CONCESSION_RATE_METRIC) {
    return false;
  }
  if (feature === Feature.CUSTOM_DATE_RANGE) {
    return false;
  }
  if (feature === Feature.CONTACT_LISTENING) {
    return false;
  }
  if (feature === Feature.CONTACT_LISTENING_WITH_CHAT) {
    return false;
  }
  if (feature === Feature.CONTACT_LISTENING_MULTITASK) {
    return false;
  }
  if (feature === Feature.CONTACT_TIMELINE) {
    return false;
  }
  if (feature === Feature.ENABLE_HISTORICAL_CCX_DETAIL) {
    return false;
  }
  if (feature === Feature.ENABLE_SDS_FORM) {
    return false;
  }
  if (feature === Feature.ENABLE_SDS_RECIPIENT_FORM_UPDATES) {
    return false;
  }
  if (feature === Feature.ENABLE_SDS_OFFROAD_DRIVER_FORM_UPDATES) {
    return false;
  }
  if (feature === Feature.OUTLIER_MANAGEMENT) {
    return false;
  }
  if (feature === Feature.ENABLE_DART_FORM) {
    return false;
  }

  return false;
};

const fetchConfig = async (
  userId: string,
  specificFlags?: Feature[]
): Promise<Response | undefined> => {
  try {
    const url = new URL(featureConfigUrl);
    const queryParams: Record<string, string> = {};

    if (specificFlags) {
      queryParams['specificFlags'] = JSON.stringify(specificFlags);
    }

    const { accessKeyId, secretAccessKey, sessionToken } =
      await Auth.currentAccess();

    const client = SigV4Client.newClient({
      accessKey: accessKeyId,
      secretKey: secretAccessKey,
      sessionToken,
      region:
        getEnvConfigByKey('featureConfigRegion') ||
        getEnvConfigByKey('region') ||
        process.env.REACT_APP_REGION,
      endpoint: url.toString(),
    });

    const signedRequest = client.signRequest({
      method: 'GET',
      path: `features/${userId}`,
      queryParams,
      body: {},
    });
    const signedHeaders = signedRequest.headers;
    const signedUrl = signedRequest.url;

    return await fetch(signedUrl, {
      headers: signedHeaders,
    });
  } catch (e) {
    logger.error('FeatureConfig.getConfig() failed.', e as Error, {});
  }
};

const getExpirationString = (): string => {
  return (Math.floor(Date.now() / 1000) + configTTL).toString();
};

const getJson = (value: string | null): any => {
  return value ? JSON.parse(value) : {};
};

const removeUserConfigByExpiration = (userConfig: any): any => {
  let updatedUserConfig: any = {};
  Object.keys(userConfig)
    .sort(function (a, b) {
      return userConfig[b][EXPIRATION] - userConfig[a][EXPIRATION];
    })
    .slice(0, 10)
    .forEach(function (key) {
      updatedUserConfig[key] = userConfig[key];
    });
  return updatedUserConfig;
};

export const updateSpecificFeatureFlag = (
  feature: Feature,
  enabled: boolean
) => {
  const featuresJson = FeatureConfig.getItem(FEATURES) || '';
  try {
    const features = featuresJson ? JSON.parse(featuresJson) : {};

    // Update the specific feature flag
    features[feature] = {
      enabled: enabled,
    };

    FeatureConfig.setItem(FEATURES, JSON.stringify(features));
  } catch (e) {
    logger.error('invalid appConfig output');
  }
  return getDefaultConfig(feature);
};

// New function to get role-based banners from config
export const getRoleBannersFromConfig = (
  feature: Feature | string,
  userRole: string
): DashboardBanner[] => {
  const featuresJson = FeatureConfig.getItem(FEATURES) || '';
  try {
    const features = featuresJson ? JSON.parse(featuresJson) : {};

    if (feature in features && features[feature].enabled) {
      const featureConfig = features[feature];

      // Handle new format with banners array
      if (featureConfig.banners && Array.isArray(featureConfig.banners)) {
        return featureConfig.banners
          .filter(
            (banner: DashboardBanner) =>
              banner.enabled && banner.enabledForRoleList.includes(userRole)
          )
          .sort(
            (a: DashboardBanner, b: DashboardBanner) => a.priority - b.priority
          );
      }
    }
  } catch (e) {
    logger.error('invalid appConfig output for role banners');
  }

  return [];
};
